import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LogOut, Menu, Save, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import AdminSidebar from '../../components/AdminSidebar';
import RichTextEditor from '../../components/RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const defaultPages = {
  'privacy-policy': {
    title: 'Privacy Policy',
    content: '<h2>Privacy Policy for Indicraft</h2><p>At Indicraft, we are committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information.</p><h3>Information We Collect</h3><p>We collect information you provide directly, such as name, email, phone number, and shipping address when you make a purchase.</p><h3>How We Use Your Information</h3><p>We use your information to process orders, provide customer support, send order updates, and improve our services.</p><h3>Data Security</h3><p>We implement industry-standard security measures to protect your data from unauthorized access.</p><h3>Contact Us</h3><p>For privacy-related questions, contact us at support@indicraft.in</p>',
    meta_title: 'Privacy Policy | Indicraft',
    meta_description: 'Read Indicraft\'s privacy policy to understand how we collect, use, and protect your personal information.'
  },
  'terms': {
    title: 'Terms & Conditions',
    content: '<h2>Terms & Conditions</h2><p>Welcome to Indicraft. By using our website, you agree to these terms and conditions.</p><h3>Use of Website</h3><p>You may use this website for personal, non-commercial purposes only. Any unauthorized use may result in termination of access.</p><h3>Product Information</h3><p>We strive to provide accurate product information, but we do not guarantee that descriptions, pricing, or images are error-free.</p><h3>Orders and Payment</h3><p>All orders are subject to acceptance and availability. Prices are in Indian Rupees (INR) and include applicable taxes.</p><h3>Limitation of Liability</h3><p>Indicraft shall not be liable for any indirect, incidental, or consequential damages arising from the use of our services.</p>',
    meta_title: 'Terms & Conditions | Indicraft',
    meta_description: 'Review the terms and conditions for using Indicraft\'s e-commerce platform.'
  },
  'shipping-policy': {
    title: 'Shipping Policy',
    content: '<h2>Shipping Policy</h2><h3>Delivery Timeframes</h3><p>We deliver across India within 5-7 business days. Metro cities may receive orders in 2-3 business days with express shipping.</p><h3>Free Shipping</h3><p>We offer FREE shipping on all orders across India. No minimum order value required!</p><h3>Order Tracking</h3><p>Once your order ships, you\'ll receive a tracking number via email and SMS. Track your order on our Track Order page.</p><h3>Delivery Partners</h3><p>We work with trusted delivery partners to ensure safe and timely delivery of your orders.</p><h3>Delivery Issues</h3><p>If you face any delivery issues, please contact our support team immediately at support@indicraft.in</p>',
    meta_title: 'Shipping Policy | Indicraft - Free Shipping Across India',
    meta_description: 'Learn about Indicraft\'s shipping policy, delivery timeframes, and free shipping offer across India.'
  },
  'refund-policy': {
    title: 'Refund & Return Policy',
    content: '<h2>Refund & Return Policy</h2><h3>7-Day Return Policy</h3><p>We offer a 7-day return policy. If you\'re not satisfied with your purchase, you can return it within 7 days of delivery.</p><h3>Return Conditions</h3><p>Products must be unused, in original packaging with all tags attached. Items showing signs of use or damage will not be accepted.</p><h3>How to Return</h3><p>Contact our support team with your order ID. We\'ll arrange a free pickup from your location.</p><h3>Refund Process</h3><p>Refunds are processed within 5-7 business days after receiving the returned product. The amount will be credited to your original payment method.</p><h3>Non-Returnable Items</h3><p>Personal care products and customized items may not be eligible for return due to hygiene and personalization reasons.</p>',
    meta_title: 'Refund & Return Policy | Indicraft',
    meta_description: 'Understand Indicraft\'s 7-day return policy, refund process, and conditions for returns.'
  },
  'cancellation-policy': {
    title: 'Cancellation Policy',
    content: '<h2>Cancellation Policy</h2><h3>Order Cancellation Window</h3><p>Orders can be cancelled within 2 hours of placement. After this window, orders enter processing and cannot be cancelled.</p><h3>How to Cancel</h3><p>To cancel an order, contact our support team immediately with your order ID via email or phone.</p><h3>Refund for Cancelled Orders</h3><p>If you paid online, the refund will be processed within 5-7 business days. COD orders don\'t require refund processing.</p><h3>Partial Cancellation</h3><p>If you ordered multiple items, you may request partial cancellation before the order ships.</p>',
    meta_title: 'Cancellation Policy | Indicraft',
    meta_description: 'Learn about Indicraft\'s order cancellation policy and how to cancel your order.'
  },
  'disclaimer': {
    title: 'Disclaimer',
    content: '<h2>Disclaimer</h2><p>The information provided on Indicraft is for general informational purposes only.</p><h3>Product Information</h3><p>While we strive to display accurate product colors and details, actual products may vary slightly due to photography and monitor settings.</p><h3>Pricing</h3><p>All prices are subject to change without notice. We reserve the right to modify or discontinue products at any time.</p><h3>External Links</h3><p>Our website may contain links to external sites. We are not responsible for the content or privacy practices of these sites.</p><h3>Limitation of Liability</h3><p>Indicraft shall not be held responsible for any damages or losses arising from the use of this website or our products.</p>',
    meta_title: 'Disclaimer | Indicraft',
    meta_description: 'Read the legal disclaimer for Indicraft\'s e-commerce website.'
  }
};

const AdminContent = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('privacy-policy');
  const [pages, setPages] = useState(defaultPages);

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (isLoggedIn !== 'true') {
      navigate('/admin', { replace: true });
      return;
    }
    loadAllPages();
  }, []);

  const loadAllPages = async () => {
    const slugs = Object.keys(defaultPages);
    for (const slug of slugs) {
      try {
        const response = await axios.get(`${API_URL}/api/pages/${slug}/full`);
        if (response.data) {
          setPages(prev => ({
            ...prev,
            [slug]: {
              title: response.data.title || defaultPages[slug].title,
              content: response.data.content || defaultPages[slug].content,
              meta_title: response.data.meta_title || defaultPages[slug].meta_title,
              meta_description: response.data.meta_description || defaultPages[slug].meta_description
            }
          }));
        }
      } catch (error) {
        // Page not found in DB, use defaults
        console.log(`Using default content for ${slug}`);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_logged_in');
    localStorage.removeItem('admin_username');
    navigate('/admin', { replace: true });
  };

  const handleContentChange = (slug, value) => {
    setPages(prev => ({
      ...prev,
      [slug]: { ...prev[slug], content: value }
    }));
  };

  const handleFieldChange = (slug, field, value) => {
    setPages(prev => ({
      ...prev,
      [slug]: { ...prev[slug], [field]: value }
    }));
  };

  const handleSave = async (pageSlug) => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/pages/${pageSlug}/full`, {
        page_slug: pageSlug,
        ...pages[pageSlug],
        last_updated: new Date().toISOString()
      });
      toast.success('Page updated successfully');
    } catch (error) {
      console.error('Failed to update page:', error);
      toast.error('Failed to update page');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (slug) => {
    setPages(prev => ({
      ...prev,
      [slug]: defaultPages[slug]
    }));
    toast.info('Reset to default content. Click Save to apply.');
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9]">
      <AdminSidebar active="content" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="admin-content">
        <div className="admin-topbar">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden"
            >
              <Menu className="w-6 h-6" />
            </Button>
            <h1 className="text-xl font-bold text-[#0f172a]" data-testid="content-title">Content Management</h1>
          </div>
          <Button
            data-testid="logout-button"
            onClick={handleLogout}
            variant="ghost"
            className="text-zinc-600"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>

        <div className="p-6">
          <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 mb-6">
                <TabsTrigger value="privacy-policy">Privacy</TabsTrigger>
                <TabsTrigger value="terms">Terms</TabsTrigger>
                <TabsTrigger value="shipping-policy">Shipping</TabsTrigger>
                <TabsTrigger value="refund-policy">Refund</TabsTrigger>
                <TabsTrigger value="cancellation-policy">Cancel</TabsTrigger>
                <TabsTrigger value="disclaimer">Disclaimer</TabsTrigger>
              </TabsList>
              
              {Object.entries(pages).map(([slug, page]) => (
                <TabsContent key={slug} value={slug} className="space-y-6 mt-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <Label>Page Title</Label>
                      <Input
                        value={page.title}
                        onChange={(e) => handleFieldChange(slug, 'title', e.target.value)}
                        className="mt-1"
                        data-testid={`page-title-${slug}`}
                      />
                    </div>
                    <div>
                      <Label>SEO Meta Title</Label>
                      <Input
                        value={page.meta_title || ''}
                        onChange={(e) => handleFieldChange(slug, 'meta_title', e.target.value)}
                        className="mt-1"
                        placeholder="Meta title for search engines"
                      />
                    </div>
                  </div>

                  <div>
                    <Label>SEO Meta Description</Label>
                    <Input
                      value={page.meta_description || ''}
                      onChange={(e) => handleFieldChange(slug, 'meta_description', e.target.value)}
                      className="mt-1"
                      placeholder="Meta description for search engines (150-160 characters)"
                    />
                  </div>

                  <div>
                    <Label>Page Content</Label>
                    <div className="mt-2 border border-zinc-200 rounded-lg">
                      <RichTextEditor
                        value={page.content}
                        onChange={(value) => handleContentChange(slug, value)}
                        placeholder="Enter page content..."
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={() => handleSave(slug)}
                      disabled={loading}
                      className="bg-[#064E3B] hover:bg-[#065F46]"
                      data-testid={`save-page-${slug}`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                      onClick={() => handleReset(slug)}
                      variant="outline"
                      className="text-zinc-600"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset to Default
                    </Button>
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContent;
